import type { InvoiceListItem } from "~/lib/queries";
import { jakartaDate, todayInJakarta, wibMidnight } from "./-fee-format";

/**
 * The period a settlement statement covers, between what an operator picks
 * (two calendar days in Jakarta) and what the API takes (two instants, both
 * inclusive: posted_at >= period_from AND posted_at <= period_to).
 */

/** The fortnight Kanata is paid on. Only the default; any period works. */
const CYCLE_DAYS = 14;

/** The last instant of a Jakarta calendar day. Microseconds, because that is
 *  Postgres's precision: anything coarser would leave a sliver of the day out. */
function wibEndOfDay(date: string): string {
  return `${date}T23:59:59.999999+07:00`;
}

/** Two YYYY-MM-DD dates as the API's inclusive bounds. */
export function periodBounds(from: string, to: string): { period_from: string; period_to: string } {
  return { period_from: wibMidnight(from), period_to: wibEndOfDay(to) };
}

/** YYYY-MM-DD plus n days, as calendar arithmetic with no zone in it. */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const shifted = new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + days));
  return shifted.toISOString().slice(0, 10);
}

/**
 * The latest day a period can end on. The API refuses a period_to in the
 * future -- a period is closed before it is invoiced -- and the end of today
 * is in the future until midnight.
 */
export function latestPeriodEnd(): string {
  return todayInJakarta(-1);
}

/**
 * Where the next statement should start and end, so cycles join with no gap:
 * the day after the latest statement that still stands, through yesterday.
 * A merchant's first statement gets the last fortnight. Void statements do
 * not count, since their settlements went back to be invoiced again.
 *
 * Gaps and overlaps are both safe -- a settlement is on at most one live
 * statement, and one outside every period waits for the next -- so this is a
 * convenience, not a rule.
 */
export function defaultPeriod(invoices: InvoiceListItem[]): { from: string; to: string } {
  const to = latestPeriodEnd();
  const lastEnd = invoices
    .filter((invoice) => invoice.status !== "void")
    .map((invoice) => jakartaDate(invoice.period_to))
    .sort()
    .at(-1);
  if (lastEnd) {
    const from = addDays(lastEnd, 1);
    return { from: from <= to ? from : to, to };
  }
  return { from: addDays(to, -(CYCLE_DAYS - 1)), to };
}

/** A statement's period as Jakarta dates, "1 Nov 2026 – 14 Nov 2026". */
export function describePeriod(invoice: Pick<InvoiceListItem, "period_from" | "period_to">): string {
  const format = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Jakarta",
  });
  return `${format.format(new Date(invoice.period_from))} – ${format.format(new Date(invoice.period_to))}`;
}
